import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const ServiceProfileEditPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    company: '',
    serviceTypes: [],
    servicesDescription: '',
    slideUrl: ''
  });
  
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const serviceTypeOptions = [
    'Consultancy', 'Regulatory', 'Legal and IP', 'Laboratory', 
    'Animal Studies', 'Clinical Studies', 'Financial', 
    'Manufacturing', 'Raw Material & API', 'Packaging'
  ];
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        
        // Get service provider profile for current user
        const { data, error: fetchError } = await supabase
          .from('service_provider_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (fetchError) throw fetchError;
        
        if (!data) {
          navigate('/profile/service/create');
          return;
        }
        
        // Set form data
        setFormData({
          company: data.company || '',
          serviceTypes: data.service_types || [],
          servicesDescription: data.services_description || '',
          slideUrl: data.slide_url || ''
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching service provider profile:', err);
        setError('Failed to load profile. Please try again.');
        setLoading(false);
      }
    };
    
    if (user) {
      fetchProfile();
    }
  }, [user, navigate]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  const handleCheckboxChange = (value) => {
    const updatedServiceTypes = formData.serviceTypes.includes(value)
      ? formData.serviceTypes.filter(type => type !== value)
      : [...formData.serviceTypes, value];
    
    setFormData({ ...formData, serviceTypes: updatedServiceTypes });
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileExt = file.name.split('.').pop().toLowerCase();
      
      // Validate file type - only accept PDF files
      if (fileExt !== 'pdf') {
        setError('Please upload a PDF file only (.pdf)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File is too large. Maximum size is 10MB.');
        return;
      }
      
      setUploadedFile(file);
      setError('');
    }
  };
  
  const uploadSlide = async () => {
    if (!uploadedFile) return null;
    
    try {
      const fileExt = uploadedFile.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `service-slides/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('slides')
        .upload(filePath, uploadedFile);
      
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('slides')
        .getPublicUrl(filePath);
      
      return publicUrl;
    } catch (err) {
      console.error('Error uploading slide:', err);
      throw new Error('Failed to upload slide. Please try again.');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    
    try {
      // Upload slide if provided
      let slideUrl = formData.slideUrl;
      
      if (uploadedFile) {
        slideUrl = await uploadSlide();
      }
      
      // Update service provider profile
      const { error: updateError } = await supabase
        .from('service_provider_profiles')
        .update({
          company: formData.company,
          service_types: formData.serviceTypes,
          services_description: formData.servicesDescription,
          slide_url: slideUrl
        })
        .eq('user_id', user.id);
      
      if (updateError) throw updateError;
      
      setSuccess('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating service provider profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return <LoadingScreen message="Loading your service provider profile..." />;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Service Provider Profile</h1>
        
        {error && (
          <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-md" role="alert">
            {error}
          </div>
        )}
        
        {success && (
          <div className="p-4 mb-6 bg-green-100 text-green-700 rounded-md" role="alert">
            {success}
          </div>
        )}
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                Company Name*
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Type of Service*
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {serviceTypeOptions.map((type) => (
                  <div key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`type-${type}`}
                      checked={formData.serviceTypes.includes(type)}
                      onChange={() => handleCheckboxChange(type)}
                      className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor={`type-${type}`} className="ml-2 block text-gray-700">
                      {type}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="servicesDescription" className="block text-gray-700 font-medium mb-2">
                List of Services and Description*
              </label>
              <textarea
                id="servicesDescription"
                name="servicesDescription"
                value={formData.servicesDescription}
                onChange={handleInputChange}
                rows="6"
                className="form-textarea w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Update Slide
              </label>
              <div className="mt-1 flex items-center">
                <input
                  type="file"
                  id="slideUpload"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Upload a PDF file with more details about your services
              </p>
              {formData.slideUrl && (
                <div className="mt-2 text-sm text-gray-600">
                  Current slide: <a href={formData.slideUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View</a>
                </div>
              )}
            </div>
            
            <div className="mb-6">
              <label htmlFor="slideUrl" className="block text-gray-700 font-medium mb-2">
                Or Provide a Link to Your Slide
              </label>
              <input
                type="url"
                id="slideUrl"
                name="slideUrl"
                value={formData.slideUrl}
                onChange={handleInputChange}
                className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="https://example.com/slide.pdf"
              />
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceProfileEditPage; 